'use client';

import { ComponentProps, ReactNode } from 'react';

import { cx } from '../cva';
import { useField } from './context';

export type FieldInputProps = ComponentProps<'input'> & {
    afterAction?: ReactNode;
    beforeAction?: ReactNode;
    hasError?: boolean;
};

export function FieldInput({
    afterAction,
    beforeAction,
    className,
    disabled,
    hasError: hasErrorProp,
    required: requiredProp,
    onChange,
    ...props
}: FieldInputProps) {
    const { id, error, name, required } = useField();

    let ariaDescription: string | undefined;

    if (error) {
        ariaDescription = `${id}-error`;
    }

    const hasError = Boolean(error);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        if (!disabled && onChange) {
            onChange(e);
        }
    };

    return (
        <label
            className={cx(
                'px-sm flex items-center justify-between gap-2 rounded-full border border-gray-100 bg-white transition',
                className,
                'has-[:focus-visible]:outline-blue has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2',
                !hasError && 'hover:border-gray-200',
                !hasError && 'focus-within:border-gray-500',
                hasError && 'border-error bg-error/10'
            )}
            data-error={hasError}
        >
            {beforeAction ?? null}
            <input
                id={id}
                name={name}
                aria-describedby={ariaDescription}
                aria-invalid={hasError || hasErrorProp}
                aria-disabled={disabled}
                disabled={disabled}
                data-disabled={disabled ? '' : undefined}
                aria-required={required || requiredProp}
                onChange={handleChange}
                className={cx(
                    'py-xs w-full flex-auto border-0 bg-transparent outline-none',
                    'placeholder:text-gray-100',
                    hasError && 'placeholder:text-red/30'
                )}
                type="text"
                {...props}
            />
            {afterAction ?? null}
        </label>
    );
}
